import { Link, Stack } from '@mui/material';
import { t } from 'i18next';
import { BackButton } from '../../../common/components/BackButton';
import { CodeSnippetCard } from '../../../common/components/CodeSnippetCard';
import { ResourceSummary } from '../../../common/components/ResourceSummary';
import { ResourceInfoItem } from '../../../common/components/ResourceSummaryInfo';
import { BaseController, BaseState } from '../../../common/controllers/BaseController';
import { WithRouterProps, withRouter } from '../../../common/higher-order-components/with-router';
import { LocationsTable } from '../../../common/locations/components/LocationsTable';
import { Location } from '../../../common/locations/models/Location';
import { LocationVariant } from '../../../common/locations/services/LocationsService';
import { PaginationResult } from '../../../common/types/pagination';
import { UrlHelper } from '../../../common/utils/UrlHelper';
import { PATHS } from '../../../routing/routes';
import { ReportOutletContextType } from '../../common/contexts/ReportOutletContext';
import { Report, ReportDone } from '../../common/models/Report';
import { ReportCertificateDetail } from '../models/ReportCertificateDetail';
import { ReportCertificatesRepository } from '../services/ReportCertificatesRepository';
import { ReportCertificateVenafiController } from './ReportCertificateVenafiController';

type State = BaseState & {
  loading: boolean;
};

type Props = WithRouterProps<ReportOutletContextType> & {};

class Controller extends BaseController<Props, State> {
  private readonly certificateService = new ReportCertificatesRepository();

  private readonly certificateId: number;

  private report: ReportDone | null;
  private certificate: ReportCertificateDetail | null;

  private initialLocations: PaginationResult<Location> | null;

  private certificateSummary: ResourceInfoItem[] | null;

  constructor(props: Props, context: unknown) {
    super(props, context);

    ({ certificateId: this.certificateId } = this.parseNumericIds(['certificateId']));

    this.report = null;
    this.certificate = null;

    this.initialLocations = null;
    this.certificateSummary = null;

    this.state = { ...super.getBaseState(), loading: true };
  }

  componentDidMount(): void {
    super.componentDidMount();

    this.loadReportContext();
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
    super.componentDidUpdate(prevProps, prevState);

    this.onReportContextChanged(prevProps.context.report, this.props.context.report);
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this.certificateService.cancelAllRequests();
  }

  render() {
    const { loading } = this.state;

    const value = this.certificate?.getValue().toLocalizedObject() ?? null;
    const encoded = this.certificate?.getEncoded() ?? null;

    return this.renderBase(
      <Stack gap={2}>
        <BackButton />
        <ResourceSummary
          title={t('detailsForCertificateId', { certificateId: this.certificateId.toString() })}
          loading={loading}
          items={this.certificateSummary}
          value={value}
          metadataValue={null}
          valueTitle=""
          metadataTitle=""
        />
        <ReportCertificateVenafiController certificate={this.certificate} />
        {(!this.initialLocations || this.initialLocations.data.length > 0) && (
          <LocationsTable
            variant={LocationVariant.Certificate}
            variantId={this.certificateId}
            initialData={this.initialLocations}
            withPrefill
            parentLoading={loading}
          />
        )}
        {!!encoded && <CodeSnippetCard title={t('pemEncodedCertificate')}>{encoded}</CodeSnippetCard>}
      </Stack>
    );
  }

  private async fetchData() {
    try {
      if (!this.report) return;

      const { certificate, initialLocations } = await this.certificateService.getCertificate(this.report.getId(), this.certificateId);
      this.certificate = certificate;
      this.initialLocations = initialLocations;
      this.certificateSummary = this.createSummary(this.report, this.certificate);
      this.setState({ loading: false });
    } catch (e: unknown) {
      this.setState({ loading: false });
      this.handleError(e, { invalidUrl: t('nonExistingCertificate') });
    }
  }

  private createSummary(report: Report, certificate: ReportCertificateDetail): ResourceInfoItem[] {
    const items: ResourceInfoItem[] = [
      { name: t('hasPrivateKey'), data: certificate.getPublicKey().getHasPrivateKey() ? t('yes') : t('no') },
      { name: t('selfSigned'), data: certificate.getIsSelfSigned() ? t('yes') : t('no') },
      { name: t('caCertificate'), data: certificate.getIsCa() ? t('yes') : t('no') },
      {
        name: t('publicKey'),
        data: (
          <Link
            href={UrlHelper.generatePath(PATHS.REPORT_KEY_LIFE_CYCLE, {
              reportId: report.getId().toString(),
              keyId: certificate.getPublicKey().getId().toString(),
            })}
            aria-label={t('a11y.report.certificates.publicKeyIdLabel', { keyId: certificate.getPublicKey().getId() }) as string}
          >
            {certificate.getPublicKey().getId()}
          </Link>
        ),
      },
    ];
    const orgCertId = certificate.getOrganizationCertificateId()?.toString() ?? null;
    if (orgCertId) {
      items.push({
        name: t('organizationCertificate'),
        data: (
          <Link
            href={UrlHelper.generatePath(PATHS.CERTIFICATE, { certificateId: orgCertId })}
            aria-label={t('a11y.report.certificates.organizationCertificateLabel', { certId: orgCertId }) as string}
          >
            {orgCertId}
          </Link>
        ),
      });
    }
    return items;
  }

  private loadReportContext() {
    // When the component mounts, consider that the previous report is null
    this.onReportContextChanged(null, this.props.context.report);
  }

  private onReportContextChanged(prevReport: Report | null, currReport: Report | null) {
    // Report hasn't changed, do nothing
    if (prevReport === currReport) return;

    if (prevReport !== null && prevReport.getId() !== currReport?.getId()) {
      // Report has changed, ReportController will redirect to Certificates
      // It would not make sense to try and load the same `certificateId` on a different Report
      return;
    }

    if (currReport instanceof ReportDone) {
      // Do nothing unless the new Report is a ReportDone
      this.report = currReport;
      this.fetchData();
    }
  }
}

export const ReportCertificateController = withRouter<ReportOutletContextType>(Controller);
